import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, getAuthHeaders, setSelectedTeamId } from '../services/api';

interface InvitationPreview {
  invitation_id: string;
  invited_email: string;
  invited_by_name: string | null;
  invited_by_email: string | null;
  expires_at: number;
  status: string;
}

interface InvitationInfo {
  invitation_id: string;
  invited_by_email: string;
  email: string;
  expires_at: number;
}

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { firebaseUser, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  const invitationId = searchParams.get('invitation_id');
  const redirectPath = `/team/accept?invitation_id=${invitationId}`;

  // Always fetch the public preview first (works without auth)
  useEffect(() => {
    if (!invitationId) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }
    fetchPreview();
  }, [invitationId]);

  // Once authenticated, fetch full invitation details
  useEffect(() => {
    if (authLoading || !firebaseUser || !invitationId) return;
    fetchInvitationDetails();
  }, [firebaseUser, authLoading, invitationId]);

  const fetchPreview = async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/team/invitations/${invitationId}/preview`);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Invalid or expired invitation');
      }
      const data = await res.json();
      setPreview(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitationDetails = async () => {
    try {
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/team/invitations/${invitationId}`, {
        headers: token ? getAuthHeaders(token) : {}
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Invalid or expired invitation');
      }

      const data = await res.json();
      setInvitationInfo(data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAccept = async () => {
    if (!invitationId) return;

    try {
      setAccepting(true);
      const token = await firebaseUser?.getIdToken();
      const res = await fetch(`${API_URL}/api/v1/team/invitations/${invitationId}/accept`, {
        method: 'POST',
        headers: token ? getAuthHeaders(token) : {}
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to accept invitation');
      }

      const data = await res.json();

      // Switch to the inviter's team so the user lands there
      if (data.team_id) {
        setSelectedTeamId(data.team_id);
      }

      // Success - navigate to dashboard with success message
      navigate('/dashboard', {
        state: { message: 'Successfully joined team!' }
      });
    } catch (err: any) {
      setError(err.message);
      setAccepting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950 p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Invalid Invitation</h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate(firebaseUser ? '/dashboard' : '/login')}
            className="px-6 py-2 bg-gradient-to-r from-primary-500 to-secondary-600 text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
          >
            {firebaseUser ? 'Go to Dashboard' : 'Go to Login'}
          </button>
        </div>
      </div>
    );
  }

  const inviterDisplay = preview?.invited_by_name || preview?.invited_by_email || 'A team member';

  // Unauthenticated: show preview with sign-in / register options
  if (!firebaseUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950 p-4">
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-lg p-8 max-w-md w-full border border-slate-200 dark:border-slate-800">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Team Invitation</h2>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 mb-6">
            <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">You've been invited by</p>
            <p className="text-lg font-semibold text-slate-900 dark:text-white">{inviterDisplay}</p>
          </div>

          <p className="text-slate-600 dark:text-slate-400 text-sm mb-6 text-center">
            Join their workspace to securely interact with AI agents, manage policies, and collaborate with your team.
          </p>

          <Link
            to={`/login?redirect=${encodeURIComponent(redirectPath)}`}
            className="block w-full px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-600 text-white rounded-lg hover:opacity-90 transition-opacity font-medium mb-3 text-center"
          >
            I have an account — Sign in
          </Link>

          <Link
            to={`/register?redirect=${encodeURIComponent(redirectPath)}&invitation_id=${invitationId}&email=${encodeURIComponent(preview?.invited_email || '')}`}
            className="block w-full px-6 py-3 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-center text-slate-700 dark:text-slate-300 font-medium"
          >
            I'm new — Create account
          </Link>

          {preview && (
            <p className="text-xs text-slate-400 dark:text-slate-500 text-center mt-4">
              Expires {new Date(preview.expires_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Authenticated: show accept/decline
  if (!invitationInfo && !error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950">
        <div className="animate-spin w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!invitationInfo) {
    return null;
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950 p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-lg p-8 max-w-md w-full border border-slate-200 dark:border-slate-800">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Team Invitation</h2>
        </div>

        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 mb-6">
          <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">You've been invited by</p>
          <p className="text-lg font-semibold text-slate-900 dark:text-white">{invitationInfo.invited_by_email}</p>
        </div>

        <p className="text-slate-600 dark:text-slate-400 text-sm mb-6 text-center">
          Join their workspace to securely interact with AI agents, manage policies, and collaborate with your team.
        </p>

        <button
          onClick={handleAccept}
          disabled={accepting}
          className="w-full px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-600 text-white rounded-lg hover:opacity-90 transition-opacity font-medium mb-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {accepting ? 'Joining Team...' : 'Join Team'}
        </button>

        <button
          onClick={() => navigate('/dashboard')}
          disabled={accepting}
          className="w-full px-6 py-3 border border-slate-300 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
        >
          Decline
        </button>

        <p className="text-xs text-slate-400 dark:text-slate-500 text-center mt-4">
          Expires {new Date(invitationInfo.expires_at).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}
